/**
 * **Bruecke zu invoice-frontend 3.52.0** — zwei Teile, die `ConversionCard` intern nutzt, das Paket aber nicht
 * exportiert. Die Konvertierungsseite dieser App (Redesign 2026-10-10, Single Column Report) braucht sie, um sich
 * GENAU wie die Paket-Karte zu verhalten. Issue an invoice-frontend (Lane ib-660):
 * `REQUIREMENTS/API/issues/2026-10-10_invoice-frontend-exporte-fuer-app-eigene-konvertierungsseite.md`.
 *
 * 🔴 Jeder Teil verschwindet, sobald das Paket ihn exportiert — dann hier den Import tauschen und die Kopie loeschen.
 *
 * 1. `normalizeResult` — nur der Teil, der auf der REST-Tuer lebt: `data` als Zeichenkette (`bufferObjectToString`,
 *    exportiert) und `mimeType` → `contentType`. Das Auspacken von tRPC-Batch-Huellen (`extractResult`) entfaellt:
 *    seit KW-003 konvertiert die Seite nur ueber REST (`useInvoiceRestClient`), der Client liefert die flache Form.
 * 2. Erkennung `toDetectionState` + Tabelle `CANDIDATE_API_FORMATS` — **Kopie** aus
 *    `invoice-frontend/src/frontend-plugin-invoice-api-frontend/lib/detect-format.ts` @ v3.52.0. Ohne sie gaebe es
 *    keine Vorbelegung des Quellformats nach „Automatisch erkennen".
 *
 * Der fruehere dritte Teil (Hinweissatz) ist weg: seit 3.53.0 exportiert das Paket `hinweisZeile` (Torsten Nr. 522).
 */
import {
  bufferObjectToString,
  type ConversionResult,
  type FormatDetectionOutcome,
  type FormatOption,
  type NormalizedConversionResult,
} from '@xhubio-saas/invoice-frontend/plugin-invoice-api'

// ─── 1. Ergebnis ────────────────────────────────────────────────────────────────────────────────

/** Das Ergebnis mit `data` als Zeichenkette — wie `normalizeResult` der Paket-Karte (ohne tRPC-Huellen). */
export function normalizeConversionResult(result: ConversionResult): NormalizedConversionResult {
  const contentType = result.contentType ?? (result as { mimeType?: string }).mimeType
  if (!result.success || !result.data) return { ...result, data: undefined, contentType }
  const data = typeof result.data === 'string' ? result.data : bufferObjectToString(result.data)
  return { ...result, data, contentType }
}

// ─── 2. Erkennung ───────────────────────────────────────────────────────────────────────────────

/**
 * Erkanntes `InboundFormat` (Parser-Vokabular) → Katalog-Token, die dafuer in Frage kommen. Kopie @ v3.52.0, siehe
 * Kopf. Mehrere Token = echte Mehrdeutigkeit: der Beleg allein sagt nicht, welches gemeint ist.
 */
export const CANDIDATE_API_FORMATS: Readonly<Record<string, readonly string[]>> = {
  XRECHNUNG_CII: ['xrechnung'],
  XRECHNUNG_UBL: ['xrechnung'],
  ZUGFERD_MINIMUM: ['zugferd'],
  ZUGFERD_BASIC: ['zugferd'],
  ZUGFERD_COMFORT: ['zugferd'],
  ZUGFERD_EN16931: ['zugferd'],
  ZUGFERD_EXTENDED: ['zugferd'],
  EBINTERFACE: ['ebinterface'],
  FACTUR_X_MINIMUM: ['facturx'],
  FACTUR_X_BASIC: ['facturx'],
  FACTUR_X_EN16931: ['facturx'],
  FACTUR_X_EXTENDED: ['facturx'],
  FATTURAPA: ['fatturapa'],
  ISDOC: ['isdoc'],
  NAV_XML: ['nav'],
  MYDATA: ['mydata'],
  FACTURAE: ['facturae'],
  VERIFACTU: ['facturae'],
  KSEF: ['ubl'],
  KSEF_FA2: ['ubl'],
  KSEF_FA3: ['ubl'],
  SAFT_PT: ['ubl'],
  EFACTURA_RO: ['ubl', 'peppol-ubl'],
  PEPPOL_BIS: ['peppol-ubl', 'ubl'],
  UBL: ['ubl', 'peppol-ubl'],
  UBL_21: ['ubl', 'peppol-ubl'],
  CII: ['zugferd', 'facturx', 'xrechnung'],
}

/** Ergebnis der Erkennung, so wie die Seite es anzeigt. */
export type DetectionState =
  | { status: 'detecting' }
  | { status: 'resolved'; option: FormatOption; detectedFormat: string }
  | { status: 'ambiguous'; candidates: FormatOption[]; detectedFormat: string }
  | { status: 'notOffered'; detectedFormat: string }
  | { status: 'unrecognized' }
  | { status: 'failed' }

/**
 * Erkennungsergebnis → Anzeigezustand. 🔴 Bei mehreren Token wird **nichts** vorbelegt — ein Vorschlag je Token,
 * nicht je Variante (die Varianten eines Tokens sind beim Lesen derselbe Aufruf).
 */
export function toDetectionState(
  detection: FormatDetectionOutcome | null,
  options: FormatOption[]
): DetectionState {
  if (!detection?.format) return { status: 'unrecognized' }
  const tokens = CANDIDATE_API_FORMATS[detection.format] ?? []
  const candidates = options.filter((o) => tokens.includes(o.apiFormat))
  if (candidates.length === 0) return { status: 'notOffered', detectedFormat: detection.format }
  const distinct = [...new Set(candidates.map((o) => o.apiFormat))]
  if (distinct.length === 1) {
    const option = candidates[0] as FormatOption
    return { status: 'resolved', option, detectedFormat: detection.format }
  }
  const perToken = distinct
    .map((token) => candidates.find((o) => o.apiFormat === token))
    .filter((o): o is FormatOption => o !== undefined)
  return { status: 'ambiguous', candidates: perToken, detectedFormat: detection.format }
}
